/**
 * Portfolio Highlights — Figma 5917:3120.
 * Maps `/phc/health-check/portfolio_highlights` (and legacy nested keys).
 *
 * API shape:
 * {
 *   asset_allocation: { large_cap_exposure, mid_cap_exposure, ..., needs_rebalancing },
 *   risk_exposure: { top_3_holding_weight, ..., largest_single_position: {symbol, weight}, risk_status },
 *   benchmark: { vs_nifty_50, ..., overall_status }
 * }
 */

export const HIGHLIGHT_TONES = {
  positive: "#45AC70",
  negative: "#C40000",
  neutral: "#FFFFFF",
};

export const FOOTER_TONES = {
  success: { bg: "#0F3D2B", color: "#57F09E" },
  warning: { bg: "#4A2F00", color: "#FFBA45" },
  info: { bg: "#0D2A3D", color: "#5DC8FF" },
};

/** Section titles + row keys/labels only (values come from API). */
const PORTFOLIO_HIGHLIGHTS_SECTIONS = {
  assetAllocation: {
    title: "Asset Allocation",
    items: [
      { key: "largeCapExposure", label: "Large Cap Exposure" },
      { key: "midCapExposure", label: "Mid Cap Exposure" },
      { key: "smallCapExposure", label: "Small Cap Exposure" },
      // { key: "etfGold", label: "ETF / Gold" },
      // { key: "shortGrossExposure", label: "Short Gross Exposure" },
      // { key: "netLongBias", label: "Net Long Bias" },
    ],
  },
  riskExposure: {
    title: "Risk Exposure",
    items: [
      { key: "top3HoldingWeights", label: "Top 3 Holding Weights" },
      { key: "top5HoldingWeights", label: "Top 5 Holding Weights" },
      { key: "top10HoldingWeights", label: "Top 10 Holding Weights" },
      // { key: "hniConcentrationIndex", label: "HNI Concentration Index" },
      { key: "effectivePositions", label: "Effective # of Positions" },
      { key: "largestSinglePosition", label: "Largest Single Positions" },
    ],
  },
  vsBenchmark: {
    title: "vs Benchmark",
    items: [
      { key: "vsNifty50", label: "vs Nifty 50" },
      // { key: "vsBseSensex30", label: "vs BSE Sensex 30" },
      { key: "vsNifty500", label: "vs Nifty 500" },
      { key: "vsNiftyMidcap150", label: "vs Nifty Midcap 150" },
      { key: "vs_nifty_smallcap_250", label: "vs Nifty Smallcap 250" },
      // { key: "overallRank", label: "Overall Rank" },
    ],
  },
};

const SECTION_KEYS = ["assetAllocation", "riskExposure", "vsBenchmark"];

/** Flat API field aliases → UI item.key */
const FIELD_ALIASES = {
  largeCapExposure: ["largeCapExposure", "large_cap_exposure"],
  midCapExposure: ["midCapExposure", "mid_cap_exposure"],
  smallCapExposure: ["smallCapExposure", "small_cap_exposure"],
  etfGold: ["etfGold", "etf_gold"],
  shortGrossExposure: ["shortGrossExposure", "short_gross_exposure"],
  netLongBias: ["netLongBias", "net_long_bias"],
  top3HoldingWeights: [
    "top3HoldingWeights",
    "top_3_holding_weight",
    "top_3_holding_weights",
  ],
  top5HoldingWeights: [
    "top5HoldingWeights",
    "top_5_holding_weight",
    "top_5_holding_weights",
  ],
  top10HoldingWeights: [
    "top10HoldingWeights",
    "top_10_holding_weight",
    "top_10_holding_weights",
  ],
  hniConcentrationIndex: [
    "hniConcentrationIndex",
    "hni_concentration_index",
  ],
  effectivePositions: [
    "effectivePositions",
    "effective_no_of_positions",
    "effective_positions",
  ],
  largestSinglePosition: [
    "largestSinglePosition",
    "largest_single_position",
  ],
  vsNifty50: ["vsNifty50", "vs_nifty_50"],
  vsBseSensex30: ["vsBseSensex30", "vs_bse_sensex_30", "vs_sensex"],
  vsNifty500: ["vsNifty500", "vs_nifty_500"],
  vsNiftyMidcap150: [
    "vsNiftyMidcap150",
    "vs_nifty_midcap_150",
    "vs_nifty_midcap",
  ],
  vsNiftyHealthcare: ["vsNiftyHealthcare", "vs_nifty_healthcare"],
  overallRank: ["overallRank", "overall_rank"],
};

const COUNT_KEYS = new Set(["effectivePositions", "hniConcentrationIndex"]);
const SIGNED_PCT_KEYS = new Set([
  "vsNifty50",
  "vsBseSensex30",
  "vsNifty500",
  "vsNiftyMidcap150",
  "vsNiftyHealthcare",
]);

const pickField = (section, key) => {
  if (!section || typeof section !== "object") return null;
  const aliases = FIELD_ALIASES[key] || [key];
  for (const alias of aliases) {
    if (section[alias] != null && section[alias] !== "") {
      return section[alias];
    }
  }
  return null;
};

const formatPct = (value, { signed = false } = {}) => {
  if (value == null || value === "") return "—";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.includes("%")) return trimmed;
    const num = Number.parseFloat(trimmed.replace("−", "-"));
    if (Number.isNaN(num)) return trimmed;
    return formatPct(num, { signed });
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    if (signed) {
      const sign = value > 0 ? "+" : value < 0 ? "−" : "";
      const abs = Math.abs(value);
      return `${sign}${abs}%`;
    }
    return `${value}%`;
  }
  return String(value);
};

const formatDisplayValue = (key, value) => {
  if (value == null || value === "") return "—";

  if (key === "largestSinglePosition" && typeof value === "object") {
    const symbol = value.symbol || value.name || "";
    const weight = value.weight ?? value.weightage;
    if (symbol && weight != null) return `${symbol} ${formatPct(weight)}`;
    if (symbol) return String(symbol);
    return formatPct(weight);
  }

  if (COUNT_KEYS.has(key)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      return String(value);
    }
    return String(value);
  }

  if (SIGNED_PCT_KEYS.has(key)) {
    return formatPct(value, { signed: true });
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    return formatPct(value);
  }

  return String(value);
};

const inferToneFromValue = (value, fallbackTone = "neutral", key) => {
  if (value == null || value === "") return fallbackTone;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value < 0) return "negative";
    if (value > 0 && SIGNED_PCT_KEYS.has(key)) return "positive";
    return fallbackTone;
  }
  const str = String(value).trim();
  if (str.startsWith("+")) return "positive";
  const cleaned = str.replace(/[%,~\s,]/g, "").replace("−", "-");
  const num = Number.parseFloat(cleaned);
  if (!Number.isNaN(num) && num < 0) return "negative";
  if (!Number.isNaN(num) && num > 0 && SIGNED_PCT_KEYS.has(key)) {
    return "positive";
  }
  return fallbackTone;
};

const resolveFooter = (apiSection) => {
  if (!apiSection || typeof apiSection !== "object") {
    return { text: "—", tone: "info" };
  }

  if (apiSection.footer?.text) {
    return {
      text: apiSection.footer.text,
      tone: apiSection.footer.tone || "info",
    };
  }

  if (apiSection.status) {
    return {
      text: apiSection.status,
      tone: apiSection.statusTone || "info",
    };
  }

  if (apiSection.risk_status) {
    return { text: apiSection.risk_status, tone: "warning" };
  }

  if (apiSection.overall_status) {
    const text = apiSection.overall_status;
    const lower = String(text).toLowerCase();
    const tone = lower.includes("under")
      ? "info"
      : lower.includes("out")
        ? "success"
        : "info";
    return { text, tone };
  }

  if (typeof apiSection.needs_rebalancing === "boolean") {
    return {
      text: apiSection.needs_rebalancing
        ? "Needs Rebalancing"
        : "Well Balanced",
      tone: apiSection.needs_rebalancing ? "warning" : "success",
    };
  }

  return { text: "—", tone: "info" };
};

const mergeSection = (sectionDef, apiSection) => {
  if (!sectionDef) return null;

  const section =
    apiSection && typeof apiSection === "object" ? apiSection : {};

  // Prefer explicit items array from API when present.
  if (Array.isArray(section.items) && section.items.length > 0) {
    const byKey = Object.fromEntries(
      section.items
        .filter((item) => item?.key)
        .map((item) => [item.key, item]),
    );

    return {
      title: section.title || sectionDef.title,
      items: sectionDef.items.map((defItem) => {
        const apiItem = byKey[defItem.key];
        const rawValue =
          apiItem?.value != null && apiItem.value !== ""
            ? apiItem.value
            : null;
        const hasValue = rawValue != null;

        return {
          key: defItem.key,
          label: apiItem?.label || defItem.label,
          value: hasValue
            ? formatDisplayValue(defItem.key, rawValue)
            : "—",
          tone: hasValue
            ? apiItem?.tone ||
              apiItem?.color ||
              inferToneFromValue(rawValue, "neutral", defItem.key)
            : "neutral",
        };
      }),
      footer: resolveFooter(section),
    };
  }

  // Flat API object — always render every defined key; missing → "—"
  const items = sectionDef.items.map((item) => {
    const apiValue = pickField(section, item.key);
    const hasApiValue = apiValue != null && apiValue !== "";

    return {
      key: item.key,
      label: item.label,
      value: hasApiValue
        ? formatDisplayValue(item.key, apiValue)
        : "—",
      tone: hasApiValue
        ? section[`${item.key}Tone`] ||
          inferToneFromValue(apiValue, "neutral", item.key)
        : "neutral",
    };
  });

  return {
    title: section.title || sectionDef.title,
    items,
    footer: resolveFooter(section),
  };
};

/**
 * Unwrap portfolio_highlights response → flat section map for merge.
 * Accepts: `{ result: [{...}] }`, `{ result: {...} }`, `[{...}]`, or a flat object.
 */
export const mapPortfolioHighlightsResponse = (payload) => {
  if (!payload || typeof payload !== "object") return null;

  const raw =
    (Array.isArray(payload) && payload[0]) ||
    (Array.isArray(payload.result) && payload.result[0]) ||
    (!Array.isArray(payload.result) && payload.result) ||
    (Array.isArray(payload.data) && payload.data[0]) ||
    payload.data ||
    payload;

  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;

  const assetAllocation =
    raw.asset_allocation || raw.assetAllocation || null;
  const riskExposure = raw.risk_exposure || raw.riskExposure || null;
  const vsBenchmark =
    raw.benchmark || raw.vs_benchmark || raw.vsBenchmark || null;

  if (!assetAllocation && !riskExposure && !vsBenchmark) {
    if (raw.assetAllocation || raw.riskExposure || raw.vsBenchmark) {
      return raw;
    }
    return null;
  }

  return {
    assetAllocation,
    riskExposure,
    vsBenchmark,
  };
};

/**
 * Map API highlights onto all section keys. Missing fields → "—".
 */
export const normalizePortfolioHighlights = (apiHighlights) => {
  const source = mapPortfolioHighlightsResponse(apiHighlights);
  if (!source) return [];

  return SECTION_KEYS.map((sectionKey) =>
    mergeSection(
      PORTFOLIO_HIGHLIGHTS_SECTIONS[sectionKey],
      source[sectionKey] || {},
    ),
  ).filter((section) => section?.items?.length);
};
